// @flow
import * as React from 'react';
// eslint-disable-next-line import/named
import type { IntlShape } from 'react-intl';
import cn from 'classnames';

// Components
import { Container, Col, Row } from 'reactstrap';
import AppointmentFlowWizard from 'components/templates/AppointmentFlowWizard/';
import AppointmentFlowFooter from 'components/molecules/AppointmentFlowFooter/';
import SelfCheckInHeader from 'components/atoms/SelfCheckInHeader/';
import Panel from 'components/atoms/Panel';
import Button from 'components/atoms/Button';
import DrawingComponent from 'components/atoms/DrawingComponent';
import Modal from 'components/atoms/Modal';
import ParseHTML from 'components/atoms/ParseHTML';
import CtaStepIndicator from 'components/atoms/CtaStepIndicator';

// Constants
import {
  SCI_ADVISOR_ROUTE,
  SCI_CONFIRMATION_ROUTE,
  SCI_ERROR_ROUTE
} from 'constants/selfCheckIn';
import { labelsStepIndicatorSCI } from 'constants/CTAConfig';

import './SignaturePage.scss';
import { resetScrollPosition } from 'utils/DOM';

const ns = 'signature-page';

export type Props = {
  goTo: (path: string) => void,
  intl: IntlShape,
  setAppointmentNotes: (?string) => void,
  setSelfCheckInSigned: string => void,
  checkIn: void => void,
  gtmUserEventSignEntered: void => void,
  gtmUserEventNotesEntered: void => void,
  gtmUserEventSignatureFullScreen: void => void,
  gtmUserEventOpenDisclaimerModal: void => void,
  gtmCompleteCheckInClick: void => void,
  signature?: string,
  appointmentNotes?: string,
  isDealerOpenNow: boolean,
  showSCIProgressBar: boolean
};

type State = {
  drawingModalKey: number,
  isModalDrawingOpen: boolean,
  isActiveRunning: boolean
};

class SignaturePage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      drawingModalKey: 0,
      isModalDrawingOpen: false,
      isActiveRunning: false
    };
    this.handleChange = this.handleChange.bind(this);
    (this: any).modalDrawing = React.createRef();
    (this: any).modalDisclaimer = React.createRef();
    (this: any).showModalDrawing = this.showModalDrawing.bind(this);
    (this: any).showModalDisclaimer = this.showModalDisclaimer.bind(this);
    (this: any).resize = this.resize.bind(this);
  }

  componentDidMount() {
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resize);
  }

  modalDrawing: React.Ref<any>;
  modalDisclaimer: React.Ref<any>;

  resize() {
    const { isModalDrawingOpen } = this.state;
    if (
      (window.orientation === -90 || window.orientation === 90) &&
      !isModalDrawingOpen
    ) {
      resetScrollPosition(130);
    } else {
      resetScrollPosition(0);
    }
  }

  handleChange = (signatureNote: string) => {
    const { setAppointmentNotes } = this.props;
    setAppointmentNotes(signatureNote);
  };

  showModalDrawing = (show: boolean = true) => {
    const { gtmUserEventSignatureFullScreen } = this.props;
    (this: any).modalDrawing.current.setShowModal(show);
    show && gtmUserEventSignatureFullScreen();
    this.setState({
      drawingModalKey: Math.random(),
      isModalDrawingOpen: show
    });
    resetScrollPosition(0);
  };

  showModalDisclaimer = (show: boolean = true) => {
    const { gtmUserEventOpenDisclaimerModal } = this.props;
    (this: any).modalDisclaimer.current.setShowModal(show);
    show && gtmUserEventOpenDisclaimerModal();
  };

  buildSignatureModal() {
    const { intl, signature } = this.props;
    return (
      <Modal
        ref={this.modalDrawing}
        onClose={() => {
          this.showModalDrawing(false);
          resetScrollPosition(130);
        }}
        size={'large'}
        showCloseButton={true}
        className={`${ns}__modal-drawing-signature`}
      >
        <DrawingComponent
          key={this.state.drawingModalKey}
          fullScreen={true}
          buttonRight={() => (
            <Button
              className={`${ns}__ok-btn px-4  mt-2`}
              onClick={() => {
                if (signature) {
                  this.showModalDrawing(false);
                  resetScrollPosition(130);
                }
              }}
              disabled={!signature}
            >
              {intl.formatMessage({
                id: 'c8.self.check.in.common.ok'
              })}
            </Button>
          )}
          signature={signature}
        />
      </Modal>
    );
  }

  buildDisclaimerModal() {
    const { intl, isDealerOpenNow } = this.props;
    const extendedHoursText = intl.formatMessage({
      id: 'c8.self.check.in.config.extended.disclaimer'
    });
    const genericFlowText = intl.formatMessage({
      id: 'c8.self.check.in.signature.disclaimer.content'
    });
    const extendedHoursExists =
      extendedHoursText !== 'c8.self.check.in.config.extended.disclaimer';
    const disclaimerText =
      isDealerOpenNow || !extendedHoursExists
        ? genericFlowText
        : extendedHoursText;
    return (
      <Modal
        ref={this.modalDisclaimer}
        size={'medium'}
        showCloseButton={true}
        className={`${ns}__modal-disclaimer`}
        title={() => (
          <div>
            {intl.formatMessage({
              id: 'c8.self.check.in.signature.disclaimer.title'
            })}
          </div>
        )}
      >
        <ParseHTML className={`${ns}__paragraphs`} html={disclaimerText} />
      </Modal>
    );
  }

  makeCheckIn = async () => {
    const {
      goTo,
      checkIn,
      signature,
      appointmentNotes,
      gtmUserEventNotesEntered,
      gtmUserEventSignEntered,
      gtmCompleteCheckInClick
    } = this.props;
    try {
      this.setState({ isActiveRunning: true });
      const checkInSuccessful = await checkIn();
      signature && gtmUserEventSignEntered();
      appointmentNotes && gtmUserEventNotesEntered();
      gtmCompleteCheckInClick();
      if (checkInSuccessful) {
        goTo(SCI_CONFIRMATION_ROUTE);
      } else {
        goTo(SCI_ERROR_ROUTE);
      }
    } catch (err) {
      this.setState({ isActiveRunning: false });
      /* eslint-disable no-console */
      console.log(err);
      /* eslint-enable no-console */
    }
  };

  getFooter = () => {
    const { intl, signature } = this.props;
    const { isActiveRunning } = this.state;
    const spinnerClass = cn(
      'appointment-flow-footer',
      `${ns}__footer-button--running `
    );
    return isActiveRunning ? (
      <div className={spinnerClass}>
        <div className="ld ld-ring ld-spin-fast" />
      </div>
    ) : (
      <AppointmentFlowFooter
        buttonDisabled={!signature}
        buttonText={intl.formatMessage({
          id: 'c8.self.check.in.common.complete.check.in'
        })}
        className={`${ns}__footer-button`}
        onClick={() => {
          this.makeCheckIn();
        }}
        ariaLabel={intl.formatMessage({
          id: 'c8.self.check.in.common.complete.check.in'
        })}
      />
    );
  };

  render() {
    const {
      intl,
      signature,
      appointmentNotes,
      showSCIProgressBar
    } = this.props;
    // TODO: remove default message when localizations are done
    return (
      <AppointmentFlowWizard
        isSelfCheckIn={true}
        className={ns}
        showSCIProgressBar={showSCIProgressBar}
        header={() =>
          showSCIProgressBar ? (
            <CtaStepIndicator
              htmlId="VehiclePageStepIndicator"
              labels={labelsStepIndicatorSCI(intl)}
              activeStep={4}
            />
          ) : (
            <SelfCheckInHeader
              label={intl.formatMessage({
                id: 'c8.self.check.in.signature.advisor.attention'
              })}
              route={SCI_ADVISOR_ROUTE}
              ariaLabel={intl.formatMessage({
                id: 'c8.aria.back.to.advisor',
                defaultMessage: 'Back to advisor page'
              })}
            />
          )
        }
        footer={() => this.getFooter()}
        body={() => (
          <React.Fragment>
            {showSCIProgressBar && (
              <SelfCheckInHeader
                label={intl.formatMessage({
                  id: 'c8.self.check.in.signature.advisor.attention'
                })}
                route={SCI_ADVISOR_ROUTE}
                ariaLabel={intl.formatMessage({
                  id: 'c8.aria.back.to.advisor',
                  defaultMessage: 'Back to advisor page'
                })}
              />
            )}
            <Container className={ns}>
              {this.buildSignatureModal()}
              {this.buildDisclaimerModal()}
              <Row>
                <Col>
                  <h1 className={`${ns}__title`}>
                    {intl.formatMessage({
                      id: 'c8.self.check.in.signature.sign.and.complete',
                      defaultMessage: 'Sign and complete check in'
                    })}
                  </h1>
                </Col>
              </Row>
              <Row>
                <Col className={`mb-2`}>
                  <h7 className={`${ns}__signature-label mr-2`}>
                    {intl.formatMessage({
                      id: 'c8.self.check.in.signature.signature'
                    })}
                  </h7>
                </Col>
              </Row>
              <Row>
                <Col className={`position-static`}>
                  <Panel className={`pb-3 position-static`}>
                    <Button
                      className={`${ns}__disclaimers-btn mx-3 my-2`}
                      buttonTheme="link"
                      onClick={this.showModalDisclaimer}
                    >
                      {intl.formatMessage({
                        id: 'c8.self.check.in.signature.disclaimers'
                      })}
                    </Button>
                    <div className={`mx-3`}>
                      <DrawingComponent
                        key={this.state.drawingModalKey}
                        fullScreen={false}
                        buttonRight={() => (
                          <div
                            className={`${ns}__open-modal-div mt-2`}
                            onClick={this.showModalDrawing}
                          >
                            <div />
                          </div>
                        )}
                        signature={signature}
                      />
                    </div>
                  </Panel>
                </Col>
              </Row>
              <Row>
                <Col>
                  <div className={`${ns}__text-area-label my-2`}>
                    {intl.formatMessage({
                      id: 'c8.self.check.in.signature.additional.notes',
                      defaultMessage: 'Additional notes for service advisor'
                    })}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col>
                  <textarea
                    className={`${ns}__text-area`}
                    onChange={ev => this.handleChange(ev.target.value)}
                    placeholder={intl.formatMessage({
                      id: 'c8.self.check.in.signature.enter.notes',
                      defaultMessage: 'Enter notes'
                    })}
                    defaultValue={appointmentNotes}
                    id="note-text-area"
                    maxLength="250"
                  />
                </Col>
              </Row>
            </Container>
          </React.Fragment>
        )}
      />
    );
  }
}

export default SignaturePage;



// WEBPACK FOOTER //
// ./src/components/pages/SelfCheckIn/SignaturePage/SignaturePage.js